






import { Component, Vue, Watch } from 'vue-property-decorator';
import initEvent from '@/events/init';
import checkout from '@/store/modules/checkout';
import cart from '@/store/modules/cart';
import payment from '@/store/modules/payment';
import store from '@/store';
import { get as dataGetter } from 'lodash';
import { AdobeAnalytics, HubSpotConfig, ShippingInfoData } from '@/store/models';
import { decode as decodeShipment } from '@/commons/shipmentMethodHelpers';
import { initState } from '@/store/stateinit';
import device from '@/store/modules/device';
import mse from '@adobe/magento-storefront-events-sdk';
import loadScript from '@/commons/scriptLoad';
import { eventInit } from '@/commons/gtmhelpers';
import user from '@/store/modules/user';
import app from '@/store/modules/app';
import { setCookie, getCookie, deleteCookie } from '@/commons/cookies';

@Component({
    components: {},
})
export default class App extends Vue {
    public hubspotConfig: HubSpotConfig = initState('hubspotConfig');
    public zendeskKey: string = store.state.options.zendesk;
    public adobeDataCollectionKey: string = store.state.options.adobeDataCollectionKey;

    public adobeAnalytics: AdobeAnalytics = initState('adobeAnalytics');

    @Watch('$route')
    public routeChange() {
        eventInit(user.isLoggedin);
    }

    private beforeMount() {
        // DataLayer
        // eventInit(user.isLoggedin);

        // Zendesk
        if (this.zendeskKey) {
            loadScript(`https://static.zdassets.com/ekr/snippet.js?key=${this.zendeskKey}`, false, false, {
                id: 'ze-snippet',
            });
        }

        // RUM
        loadScript('https://rum.hlx.page/.rum/@adobe/helix-rum-js@^2/dist/rum-standalone.js', false, true);

        // Adobe Data Collection
        if (this.adobeDataCollectionKey) {
            loadScript(
                `https://assets.adobedtm.com/59052d39661d/154345042aff/launch-${this.adobeDataCollectionKey}.min.js`,
                true,
                false
            );
        }

        if (!device.isIosApp) {
            document.body.classList.add('show-cookiebot-banner');
        }
    }

    private mounted() {
        // PayPal SDK
        const paypalConfigs = store.state.options.payment.paypal;
        if (paypalConfigs && paypalConfigs.active) {
            loadScript(paypalConfigs.sdk_url, false, false, paypalConfigs.sdk_attributes);
            if (paypalConfigs.vault_enabled && paypalConfigs.vault_id) {
                payment.additionalData.paypal_tokenization.vaulted_email = paypalConfigs.vault_email;
                payment.additionalData.paypal_tokenization.vaulted_id = paypalConfigs.vault_id;
            }
        }

        // HiPay SDK
        const hiPayConfigs = store.state.options.payment.hipay;
        if (hiPayConfigs && hiPayConfigs.active) {
            loadScript(hiPayConfigs.sdk_url, false, false);
        } else {
            const hiPayApplePayConfigs = store.state.options.payment.hipay_applepay;
            if (hiPayApplePayConfigs && hiPayApplePayConfigs.active) {
                loadScript(hiPayApplePayConfigs.sdk_url, false, false);
            }
        }

        // Hubspot chatbot
        if (this.hubspotConfig.active && this.hubspotConfig.chatbot.enabled) {
            if (
                user.isLoggedin &&
                this.hubspotConfig.chatbot.customerGroups.includes(user.customer.extension_attributes.pu_customer_group)
            ) {
                document.body.classList.add('show-hubspot-chatbot');
            }
        }

        // in npm run serve
        if (process.env.NODE_ENV !== 'production') {
            let resolveProm: any;
            let rejectProm: any;

            const promise = new Promise((resolve, reject) => {
                resolveProm = resolve;
                rejectProm = reject;
            });

            let initPayload: any;
            // eslint-disable-next-line no-console
            console.log('Serve app as', process.env.VUE_APP_FLOW);

            if (process.env.VUE_APP_FLOW === 'guest') {
                initPayload = { cart: process.env.VUE_APP_CARTID, resolveProm, rejectProm };
            } else if (process.env.VUE_APP_FLOW === 'logged') {
                initPayload = {
                    user: process.env.VUE_APP_USER,
                    pass: process.env.VUE_APP_PASS,
                    resolveProm,
                    rejectProm,
                };
            } else if (process.env.VUE_APP_FLOW === 'loggedsess') {
                if (localStorage.getItem('usertoken')) {
                    initPayload = { token: localStorage.getItem('usertoken'), resolveProm, rejectProm };
                } else {
                    initPayload = { cart: process.env.VUE_APP_CARTID, resolveProm, rejectProm };
                }
            } else {
                throw Error('Flow not supported');
            }

            this.$eventBus.$emit(initEvent, initPayload);

            promise.then(() => {
                if (!cart.shippingAssignementAddress && !cart.isEmpty) {
                    checkout.assignShippingAddress(checkout.shippingAddress);
                }
            });
        } else {
            // in npm run build
            this.$eventBus.$emit(initEvent, {});

            // eslint-disable-next-line no-underscore-dangle
            const initialState = window.__INITIAL_STATE__ || {};

            // watch initial state props and commit mutations
            const watchInitialState: any = {
                'cart.id': { mutation: 'cart/setCart', payload: 'cart' },
            };

            for (const k in watchInitialState) {
                if (dataGetter(initialState, k)) {
                    store.commit(watchInitialState[k].mutation, dataGetter(initialState, watchInitialState[k].payload));
                }
            }

            // handle last order shipping/payment - https://support.webformat.com/issues/21299
            if (initialState.lastOrderData) {
                const lastPaymentMethod = initialState.lastOrderData.paymentMethod;

                if (lastPaymentMethod && checkout.hasPaymentMethod(lastPaymentMethod) && !cart.isEmpty) {
                    checkout.setPayment(lastPaymentMethod);
                }

                const lastShipmentMethod = initialState.lastOrderData.shippingMethod;

                if (
                    lastShipmentMethod &&
                    checkout.hasShippingMethod(lastShipmentMethod) &&
                    !checkout.shippingAddress.customer_address_id
                ) {
                    const data: ShippingInfoData = {
                        address: checkout.defaultShippingAddress,
                        shippingmethod: decodeShipment(lastShipmentMethod),
                    };

                    checkout.setSelectedShipment(lastShipmentMethod);
                    checkout.setShippingInfo(data);
                }
            }

            if (this.adobeAnalytics.shoppingCart) {
                mse.context.setShoppingCart(this.adobeAnalytics.shoppingCart);
            }
            if (this.adobeAnalytics.storeFrontInstance) {
                mse.context.setStorefrontInstance(this.adobeAnalytics.storeFrontInstance);
            }
            if (this.adobeAnalytics.dataServicesExtension) {
                mse.context.setDataServicesExtension(this.adobeAnalytics.dataServicesExtension);
            }
            if (this.adobeAnalytics.magentoExtensionVersion) {
                mse.context.setMagentoExtension(this.adobeAnalytics.magentoExtensionVersion);
            }

            if (app.isApp) {
                const cookieName = 'wf_app_logged_user';
                const cookieLifetime = 3600;
                const storeCode = store.state.options.store_code;
                if (user.isLoggedin) {
                    if (getCookie(cookieName) == null) {
                        setCookie(cookieName, Date.now().toString(), cookieLifetime, '/'+storeCode);
                    }
                } else {
                    if (getCookie(cookieName) !== null) {
                        deleteCookie(cookieName);
                    }
                }
            }
        }
    }
}
